import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { BehaviorSubject, delay, finalize } from 'rxjs';
import { ModalConfig } from 'src/app/shared/components/modal/modal-config';
import { TableHeader } from 'src/app/shared/models/table';
import { KNOWLEDGE_BANK_MESSAGES } from 'src/app/util/constantes-mensajes';
import { HelperFunctions } from 'src/app/util/util-functions';
import { v4 as uuidv4 } from 'uuid';
import { AreaKnowledgeBank } from '../area-knowledge-bank';
import { AreaKnowledgeBankService } from '../area-knowledge-bank.service';
@Component({
  selector: 'app-manage-area-knowledge-bank',
  templateUrl: './manage-area-knowledge-bank.component.html',
  styleUrls: ['./manage-area-knowledge-bank.component.scss'],
  standalone: false
})
export class ManageAreaKnowledgeBankComponent implements OnInit, OnDestroy {
  // This property gets the report group to edit
  @Input() areaKnowledgeBank: AreaKnowledgeBank;
  // Report group information form
  areaKnowledgeBankForm: FormGroup;
  // Variable to save filter values
  search$ = new BehaviorSubject<TableHeader | undefined>(undefined);
  // Variable to set modal information
  public modalConfig: ModalConfig;
  // Variable to show/hide spinner
  isLoading = false;
  // Variable to unsubscribe pending subscriptions
  isAlive = true;
  // Contains error icon
  faCircleXmark = faCircleXmark;

  constructor(
    private areaKnowledgeBankService: AreaKnowledgeBankService,
    private ngbActiveModal: NgbActiveModal,
    private ngxUiLoaderService: NgxUiLoaderService,
  ) { }

  ngOnInit(): void {
    this.modalConfig = {
      modalTitle: this.areaKnowledgeBank ? "Editar areas del banco de conocimiento" : "Crear areas del banco de conocimiento",
      ...this.modalConfig,
    };
    this.initForm();
  }

  /**
  * A function that inits the report group form
  * @returns None
  */
  initForm(): void {
    this.areaKnowledgeBankForm = new FormGroup({
      uuid: new FormControl(this.areaKnowledgeBank?.uuid || uuidv4()),
      name: new FormControl(this.areaKnowledgeBank?.nombreArea || null, [Validators.required, Validators.maxLength(255)]),
      description: new FormControl(this.areaKnowledgeBank?.description || null, [Validators.required, Validators.maxLength(255)]),
    });
  }

  /**
  * This function checks if the form control is invalid
  * @param {string} name
  * @returns {boolean}
  */
  hasErrors(name: string): boolean {
    const control = this.areaKnowledgeBankForm.get(name) as FormControl;
    return HelperFunctions.controlIsInvalid(control);
  }

  /**
  * A function to submit the report group object
  * @returns None
  */
  onSubmit() {
    if (this.areaKnowledgeBankForm.valid) {
      const groupData = this.areaKnowledgeBankForm.getRawValue();
      const areaKnowledgeBank: AreaKnowledgeBank = {
        uuid: groupData.uuid,
        nombreArea: groupData.name,
        description: groupData.description
      };
      this.saveAreaKnowledgeBank(areaKnowledgeBank);
    } else {
      this.areaKnowledgeBankForm.markAllAsTouched();
    }
  }

  /**
  * This function creates or updates a report group
  * @param {AreaKnowledgeBank} areaKnowledgeBank
  */
  saveAreaKnowledgeBank(areaKnowledgeBank: AreaKnowledgeBank) {
    this.isLoading = true;
    this.ngxUiLoaderService.start('manage-area-knowledge-bank');
    const subscription = this.areaKnowledgeBank ? this.areaKnowledgeBankService.updateAreaKnowledgeBank(areaKnowledgeBank) : this.areaKnowledgeBankService.createAreaKnowledgeBank(areaKnowledgeBank);
    subscription.pipe(
      delay(500),
      finalize(() => { this.isLoading = false; this.ngxUiLoaderService.stop('manage-area-knowledge-bank'); }))
      .subscribe({
        next: () => {
          this.ngbActiveModal.close({ message: KNOWLEDGE_BANK_MESSAGES.AREAS.UPDATE.SUCCESS, confirmTitle: "Aceptar", isError: false });
        },
        error: (error) => {
          this.ngbActiveModal.close({ message: error.error.message, confirmTitle: "Cerrar", isError: true });
        }
      });
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}